import type { LanguageCode } from '../lib/languages'
import { GRAMMAR_ZH } from './grammarZh'
import { GRAMMAR_DE } from './grammarDe'
import { GRAMMAR_FR } from './grammarFr'
import { GRAMMAR_ES } from './grammarEs'
import { GRAMMAR_IT } from './grammarIt'

/**
 * The rules behind the islands, stated the way a traditional course states them.
 *
 * The method here is chunks first: you own a block before you analyze it. But
 * plenty of adult learners find a sentence far easier to keep once they know
 * why it is built that way — that "tomorrow" goes before "at home" in Chinese,
 * or that German keeps its verb second — and a rule you can name is a rule you
 * can notice in your own islands. So this is a reference, not a course: no
 * drills, no schedule, nothing added to the review queue.
 *
 * Every rule is a formula plus examples, and the examples are written in a
 * small markup so each piece can be tied back to the formula:
 *
 *     '[我](Subject)[明天](Time)[在家](Place)[工作](Verb)。'
 *     'Das ist ein [neu**er**](Adjective) Wagen.'
 *
 * `[text](Slot)` tags a piece with the formula slot it fills, and `**text**`
 * picks out the letters that carry the grammar, such as an ending. Everything
 * else is plain text. The formula's slot names are the only tags allowed, and
 * grammar.test.ts holds every example to that, so a label on screen always
 * matches a chip in the formula above it.
 */

/** A fixed word in a formula (了, 把, ne…pas), as opposed to a role like "Time". */
export interface LiteralSlot {
  word: string | string[]
}

export type PatternSlot = string | LiteralSlot

export interface GrammarExample {
  /** Marked-up sentence. See the note at the top of this file. */
  text: string
  /** Mandarin only: tone-marked, one syllable per character, as spoken. */
  pinyin?: string
  english: string
}

/** The mistake an English speaker actually makes, and its correction. */
export interface Pitfall {
  wrong: string
  right: string
  wrongPinyin?: string
  rightPinyin?: string
  why: string
}

export type GrammarTopic =
  | 'order'
  | 'verbs'
  | 'nouns'
  | 'questions'
  | 'negation'
  | 'particles'
  | 'linking'
  | 'register'

export interface GrammarRule {
  /** Unique across every language, and stable: it is the React key. */
  id: string
  /** Same 1–6 scale as islands: HSK for Mandarin, A1–C2 for the rest. */
  level: number
  topic: GrammarTopic
  title: string
  /** The rule in the fewest words that are still true. */
  rule: string
  pattern: PatternSlot[]
  /**
   * `sequence` is a formula read left to right, joined by +. `contrast` is a
   * set of alternatives — ser versus estar — with no order implied.
   */
  shape?: 'sequence' | 'contrast'
  /** At least two. The first one is always on screen. */
  examples: GrammarExample[]
  pitfall?: Pitfall
  note?: string
}

export const TOPIC_LABEL: Record<GrammarTopic, string> = {
  order: 'Word order',
  verbs: 'Verbs',
  nouns: 'Nouns',
  questions: 'Questions',
  negation: 'Negation',
  particles: 'Particles',
  linking: 'Linking ideas',
  register: 'Register',
}

const GRAMMAR: Partial<Record<LanguageCode, GrammarRule[]>> = {
  zh: GRAMMAR_ZH,
  de: GRAMMAR_DE,
  fr: GRAMMAR_FR,
  es: GRAMMAR_ES,
  it: GRAMMAR_IT,
}

/** Every rule for a language, in teaching order. Empty when there are none. */
export function grammarFor(language: string): GrammarRule[] {
  return GRAMMAR[language as LanguageCode] ?? []
}

/** Languages with any grammar at all — the rail link exists only for these. */
export function hasGrammar(language: string): boolean {
  return grammarFor(language).length > 0
}

/** One run of an example: plain or tagged, with emphasis spans inside. */
export interface Piece {
  text: string
  /** The formula slot this piece fills, or null for plain text. */
  slot: string | null
}

const TAG = /\[([^\]]+)\]\(([^)]+)\)/g

/** Split marked-up text into plain and tagged pieces, in order. */
export function parsePieces(source: string): Piece[] {
  const pieces: Piece[] = []
  let last = 0
  for (const match of source.matchAll(TAG)) {
    const at = match.index ?? 0
    if (at > last) pieces.push({ text: source.slice(last, at), slot: null })
    pieces.push({ text: match[1], slot: match[2] })
    last = at + match[0].length
  }
  if (last < source.length) pieces.push({ text: source.slice(last), slot: null })
  return pieces
}

/** A run of text with its emphasized stretches marked. */
export interface Run {
  text: string
  strong: boolean
}

/** Split on `**…**`. An unclosed marker is left as literal text. */
export function parseEmphasis(text: string): Run[] {
  const runs: Run[] = []
  const bits = text.split('**')
  // An even number of pieces means an odd number of markers: one is unclosed.
  if (bits.length % 2 === 0) return [{ text, strong: false }]
  bits.forEach((bit, i) => {
    if (bit) runs.push({ text: bit, strong: i % 2 === 1 })
  })
  return runs
}

/** What is actually said: markup stripped, emphasis removed. */
export function plainText(source: string): string {
  return parsePieces(source)
    .map((p) => p.text)
    .join('')
    .replaceAll('**', '')
}

/** The words a literal slot stands for. */
export function literalWords(slot: LiteralSlot): string[] {
  return Array.isArray(slot.word) ? slot.word : [slot.word]
}

/** What a slot is called on screen: "Time", or "在 / 正在". */
export function slotLabel(slot: PatternSlot): string {
  return typeof slot === 'string' ? slot : literalWords(slot).join(' / ')
}

/**
 * Which formula slot a tag refers to, or -1.
 *
 * A role matches by name. A literal matches any of its words, so a formula
 * offering 在 / 正在 accepts an example tagged with either.
 */
export function slotIndex(rule: GrammarRule, tag: string): number {
  return rule.pattern.findIndex((slot) =>
    typeof slot === 'string' ? slot === tag : literalWords(slot).includes(tag),
  )
}

/**
 * The color a role takes, counting roles only.
 *
 * Literals are not colored — they are the grammar itself and are set in bold
 * instead — so the first role is always color 1 whatever precedes it. Six
 * colors cycle, which no formula here comes close to exhausting.
 */
export function roleColor(rule: GrammarRule, index: number): number | null {
  const slot = rule.pattern[index]
  if (slot === undefined || typeof slot !== 'string') return null
  const roles = rule.pattern.slice(0, index + 1).filter((s) => typeof s === 'string').length
  return ((roles - 1) % SLOT_COLORS) + 1
}

export const SLOT_COLORS = 6

/**
 * The rules at or below a level, and the rest.
 *
 * The same bargain the islands make: nothing hidden and nothing locked, but the
 * level decides what is in front of you. A beginner opening this page should
 * meet 吗 and measure words, not 与其…不如.
 */
export function splitGrammar(
  rules: GrammarRule[],
  level: number,
): { ready: GrammarRule[]; ahead: GrammarRule[] } {
  return {
    ready: rules.filter((r) => r.level <= level),
    ahead: rules.filter((r) => r.level > level),
  }
}
